// Old mail from a Google Takeout file (P5 part 3, decision 148). The owner drops the .mbox file on Papers; the browser
// reads it here, in slices (a file of gigabytes never sits in memory whole), takes each mail apart and hands back the
// papers attached to it with the mail's sender and the check Google wrote at delivery, as the file has them. The
// server judges the sender from those (the file is the owner's word for them, decision 148).

const F = 0x46; // "F" of "From "
const NL = 0x0a;
const isFromLine = (d: Uint8Array, i: number) => d[i] === F && d[i + 1] === 0x72 && d[i + 2] === 0x6f && d[i + 3] === 0x6d && d[i + 4] === 0x20 && (i === 0 || d[i - 1] === NL);

const join = (a: Uint8Array, b: Uint8Array) => {
  if (a.length === 0) return b;
  const out = new Uint8Array(a.length + b.length);
  out.set(a);
  out.set(b, a.length);
  return out;
};

/** A mail of the file: Gmail's own id for it and the day it arrived (from its "From " line), and the mail itself. */
export interface RawMail {
  gmailId: string;
  receivedAt: Date | null;
  raw: Uint8Array;
}

/**
 * The line Google Takeout starts each mail with: "From <Gmail's id>@xxx <day it arrived>", e.g.
 * "From 1781234567890123456@xxx Thu Oct 24 15:33:00 +0000 2024". Only that form starts a mail: a line of a mail's text
 * that begins "From " is not one, and a file that is not Google's never splits.
 */
const SEPARATOR = /^From (\d{6,25})@xxx +([A-Z][a-z]{2} [A-Z][a-z]{2} +\d{1,2} \d{2}:\d{2}:\d{2}(?: [+-]\d{4})? \d{4})\s*$/;
const LONGEST_SEPARATOR = 200;
/** Gmail takes no mail over 50 MB: more than this with no next mail is not a Takeout file */
export const LONGEST_MAIL = 64 * 1024 * 1024;

export class NotTakeout extends Error {}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** The day Gmail received the mail, from its "From " line's date: "Thu Oct 24 15:33:00 +0000 2024". */
export function fromLineDate(line: string): Date | null {
  const m = /(\w{3}) +(\d{1,2}) (\d{2}):(\d{2}):(\d{2})(?: ([+-])(\d{2})(\d{2}))? (\d{4})\s*$/.exec(line);
  if (!m) return null;
  const month = MONTHS.indexOf(m[1]!);
  if (month < 0) return null;
  const utc = Date.UTC(Number(m[9]), month, Number(m[2]), Number(m[3]), Number(m[4]), Number(m[5]));
  const off = m[6] ? (Number(m[7]) * 60 + Number(m[8])) * (m[6] === '-' ? -1 : 1) : 0;
  const d = new Date(utc - off * 60_000);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * The mails of a Takeout mbox file, one at a time. `onRead` is told how many bytes of the file are read. A file that
 * does not start as Google writes it, or a "mail" past 64 MB, stops with NotTakeout.
 */
export async function* mailsOf(file: Blob, opts: { chunk?: number; onRead?: (bytes: number) => void } = {}): AsyncGenerator<RawMail> {
  const size = opts.chunk ?? 8 * 1024 * 1024;
  const latin1 = new TextDecoder('latin1');
  let pending: Uint8Array = new Uint8Array(0);
  let head: RegExpExecArray | null = null; // the separator of the mail in `pending`
  let scanFrom = 0;
  const mail = (sep: RegExpExecArray, chunk: Uint8Array): RawMail => {
    const eol = chunk.indexOf(NL);
    return { gmailId: sep[1]!, receivedAt: fromLineDate(sep[2]!), raw: chunk.subarray(eol < 0 ? chunk.length : eol + 1) };
  };
  for (let offset = 0; offset < file.size; offset += size) {
    const next = new Uint8Array(await file.slice(offset, offset + size).arrayBuffer());
    const data = join(pending, next);
    let start = 0;
    let undecided: number | null = null;
    for (let i = scanFrom; i + 5 <= data.length; i += 1) {
      if (!isFromLine(data, i)) continue;
      const eol = data.indexOf(NL, i);
      if (eol < 0 && data.length - i < LONGEST_SEPARATOR) {
        undecided = i; // the line goes on in the next slice
        break;
      }
      if (eol < 0 || eol - i > LONGEST_SEPARATOR) continue;
      const sep = SEPARATOR.exec(latin1.decode(data.subarray(i, eol)).replace(/\r$/, ''));
      if (!sep) continue;
      if (head) yield mail(head, data.subarray(start, i));
      head = sep;
      start = i;
    }
    if (!head && (undecided === null || undecided > 0) && data.length >= LONGEST_SEPARATOR) throw new NotTakeout('not a Google Takeout mail file');
    pending = data.slice(start);
    if (pending.length > LONGEST_MAIL) throw new NotTakeout('a mail larger than 64 MB');
    // a line still undecided is looked at again with the next slice, and so are the bytes too near the end to hold a
    // whole "From "; the mail's own "From " at the start of what is kept is not
    scanFrom = undecided !== null ? undecided - start : Math.max(1, pending.length - 4);
    opts.onRead?.(Math.min(offset + size, file.size));
  }
  if (head && pending.length > 0) yield mail(head, pending);
  else if (!head && pending.length > 0) throw new NotTakeout('not a Google Takeout mail file');
}

export const PAPER_TYPES = new Set(['application/pdf', 'image/jpeg', 'image/png', 'image/webp']);
export const PAPER_MAX = 15 * 1024 * 1024;
const PHOTO_MAX = 5 * 1024 * 1024;
/** a picture set inside the mail's text (a logo, a signature) is not a paper; a photo of one is bigger than this */
const SMALL_PICTURE = 50 * 1024;
/** Gmail's folders whose mail is never a vendor's paper */
const NOT_PAPERS = new Set(['spam', 'trash', 'chat']);

const typeOf = (mimeType: string, filename: string): string | null => {
  const m = mimeType.toLowerCase();
  if (PAPER_TYPES.has(m)) return m;
  // mail programs often send a PDF as "octet-stream": the name tells (the server checks the first bytes)
  const n = filename.toLowerCase();
  if (n.endsWith('.pdf')) return 'application/pdf';
  if (/\.jpe?g$/.test(n)) return 'image/jpeg';
  if (n.endsWith('.png')) return 'image/png';
  return null;
};

export interface MailPaper {
  index: number;
  filename: string;
  mimeType: string;
  body: Uint8Array;
}

export interface OldMail {
  gmailId: string;
  receivedAt: Date | null;
  /** the first From header as written (the server takes the address out of it, as for the mailbox) */
  from: string | null;
  /** the top Authentication-Results header: the one Google wrote when the mail reached the mailbox */
  auth: string | null;
  /** another mail sits inside this one: its sender was checked by no one */
  nested: boolean;
  /** in Spam or Trash */
  junk: boolean;
  papers: MailPaper[];
  /** papers too big for the reader */
  tooBig: number;
}

/** The Gmail labels of a mail, from the header Takeout writes before the mail's own ("X-Gmail-Labels: Inbox,Spam"). */
const labelsOf = (raw: Uint8Array): string[] => {
  const top = new TextDecoder('latin1').decode(raw.subarray(0, 4096));
  const m = /^X-Gmail-Labels: *(.*(?:\r?\n[ \t].*)*)/im.exec(top.split(/\r?\n\r?\n/)[0] ?? '');
  return m ? m[1]!.replace(/\r?\n[ \t]+/g, ' ').split(',').map((l) => l.trim().toLowerCase()) : [];
};

/** One mail taken apart: who sent it, Google's check and the papers attached to it. */
export async function readMail(mail: RawMail): Promise<OldMail> {
  const junk = labelsOf(mail.raw).some((l) => NOT_PAPERS.has(l));
  const base = { gmailId: mail.gmailId, receivedAt: mail.receivedAt, junk };
  if (junk) return { ...base, from: null, auth: null, nested: false, papers: [], tooBig: 0 };
  // loaded only when a file is dropped: the phone app never carries it
  const { default: PostalMime } = await import('postal-mime');
  // the mail on its own buffer (a view into the slice read from the file is not always read right); a mail inside the
  // mail stays one closed attachment, never opened: its papers' sender was checked by no one
  const email = await PostalMime.parse(mail.raw.slice().buffer, { attachmentEncoding: 'arraybuffer', forceRfc822Attachments: true, maxNestingDepth: 50 });
  // the first header of each, as the mailbox's reader takes it (a mail may carry two)
  const header = (key: string) => email.headers.find((h) => h.key === key)?.value ?? null;
  const nested = email.attachments.some((a) => a.mimeType.toLowerCase() === 'message/rfc822') || /content-type:\s*multipart\/digest/i.test(new TextDecoder('latin1').decode(mail.raw));
  const papers: MailPaper[] = [];
  let tooBig = 0;
  email.attachments.forEach((a, index) => {
    if (!a.filename) return;
    const mimeType = typeOf(a.mimeType, a.filename);
    if (!mimeType) return;
    const body = a.content instanceof ArrayBuffer ? new Uint8Array(a.content) : typeof a.content === 'string' ? new TextEncoder().encode(a.content) : a.content;
    if (body.length === 0) return;
    if (mimeType !== 'application/pdf' && a.related && body.length < SMALL_PICTURE) return;
    if (body.length > (mimeType === 'application/pdf' ? PAPER_MAX : PHOTO_MAX)) {
      tooBig += 1;
      return;
    }
    papers.push({ index, filename: a.filename, mimeType, body });
  });
  return { ...base, from: header('from'), auth: header('authentication-results'), nested, papers, tooBig };
}

/** the server's key for one paper of one mail: Gmail's id for the mail (never one a sender wrote) and its place */
export const mailRef = (gmailId: string, index: number) => `${gmailId}#${index}`;
